import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogActions,
  Divider,
  TextField,
} from '@mui/material';

import {
  getEventRuntime,
  startEvent,
  endEvent,
  adjustScore,
  undoScore,
  resetGame,
} from '../../services/event-runtime.service';
import {
  fetchEventGet,
  fetchEventStatusApply,
  fetchEventStatusConfig,
  fetchEventStatusPhrase,
} from '../../services/event.service';
import { fetchOrganisationTeamsGet } from '../../services/organisation.service';
import { IOrganisationTeamItemSummary } from '../../types/@organisation';

type Runtime = {
  eventId: string;
  name: string;
  title?: string;
  baseName?: string;
  slogan?: string;
  gameType: string;
  phase?: 'pre' | 'during' | 'post';
  gamePeriod: number;
  maxGamePeriods: number;
  inPlay: boolean;
  periodStatus?: 'ready' | 'running' | 'ended';
  labels?: {
    currentPeriodName: string;
    nextPeriodName: string;
    startActionLabel: string;
    endActionLabel: string;
    gameOverLabel: string;
    continuous?: boolean;
  };
  score?: {
    left: number;
    right: number;
  };
  timer: {
    startedAt: number | null;
    accumulatedMs: number;
    running: boolean;
  };
  permissions: {
    canControlTimer: boolean;
    canViewTimer: boolean;
  };
  log: { ts: number; action: string; note?: string }[];
};

type StatusPlayer = {
  ownerId: string;
  name: string;
  statement: string | null;
  itemCount: number;
  qdosItemIds: string[];
};

type ActivePanel = 'default' | 'status' | 'score' | 'posts';

type StatusConfigButton = {
  label: string;
  sx?: Record<string, any>;
};

type StatusConfig = {
  phase?: 'pre' | 'during' | 'post';
  firstOptions: StatusConfigButton[];
  refinements: Record<string, StatusConfigButton[]>;
};

function splitTeamsFromTitle(title: string): { leftTeam: string; rightTeam: string } | null {
  const parts = title.split(/\s+vs\s+/i);

  if (parts.length !== 2) {
    return null;
  }

  return {
    leftTeam: parts[0].trim(),
    rightTeam: parts[1].trim(),
  };
}

function isRuntimeShape(value: any): value is Runtime {
  return !!value &&
    typeof value === 'object' &&
    typeof value.eventId === 'string' &&
    typeof value.name === 'string' &&
    typeof value.gameType === 'string' &&
    typeof value.gamePeriod === 'number' &&
    typeof value.maxGamePeriods === 'number' &&
    typeof value.inPlay === 'boolean' &&
    !!value.timer &&
    typeof value.timer === 'object' &&
    typeof value.timer.accumulatedMs === 'number' &&
    typeof value.timer.running === 'boolean' &&
    !!value.permissions &&
    typeof value.permissions === 'object' &&
    typeof value.permissions.canControlTimer === 'boolean' &&
    typeof value.permissions.canViewTimer === 'boolean' &&
    Array.isArray(value.log);
}

async function fetchAllEvents(): Promise<any[]> {
  const firstPage = await fetchEventGet(1);
  const totalPages = Math.max(1, Number(firstPage?.pages || 1));
  const allEvents = Array.isArray(firstPage?.data) ? [...firstPage.data] : [];

  if (totalPages > 1) {
    const remainingPages = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, index) => fetchEventGet(index + 2)),
    );

    remainingPages.forEach((page) => {
      if (Array.isArray(page?.data)) {
        allEvents.push(...page.data);
      }
    });
  }

  return allEvents;
}

async function fetchEventPlayers(eventId: string): Promise<StatusPlayer[]> {
  const allEvents = await fetchAllEvents();

  const currentEvent = allEvents.find(
    (item: any) =>
      String(item?._id || '') === String(eventId) ||
      String(item?.qdosEventId || '') === String(eventId),
  );

  const organisationId = String(
    currentEvent?.organisation?._id ||
    currentEvent?.organisationId ||
    '',
  ).trim();

  const teamId = String(
    currentEvent?.team?._id ||
    currentEvent?.teamId ||
    '',
  ).trim();

  if (!organisationId || !teamId) {
    return [];
  }

  const teamsState = await fetchOrganisationTeamsGet(organisationId);
  const matchingTeam = (teamsState?.teams || []).find(
    (team: any) => String(team?.teamId || '') === teamId,
  );

  if (!matchingTeam?.players?.items?.length) {
    return [];
  }

  const playersByOwner = new Map<string, StatusPlayer>();

  (matchingTeam.players.items as IOrganisationTeamItemSummary[]).forEach((item) => {
    const ownerId = String(item.ownerId || item.qdosItemId || '').trim();
    const ownerName = String(item.ownerName || 'Unnamed player').trim() || 'Unnamed player';
    const qdosItemId = String(item.qdosItemId || '').trim();

    if (!ownerId) {
      return;
    }

    if (!playersByOwner.has(ownerId)) {
      playersByOwner.set(ownerId, {
        ownerId,
        name: ownerName,
        statement: item.statement || null,
        itemCount: 0,
        qdosItemIds: [],
      });
    }

    const existing = playersByOwner.get(ownerId)!;
    existing.itemCount += 1;

    if (qdosItemId && !existing.qdosItemIds.includes(qdosItemId)) {
      existing.qdosItemIds.push(qdosItemId);
    }

    if (!existing.statement && item.statement) {
      existing.statement = item.statement;
    }
  });

  return Array.from(playersByOwner.values()).sort((a, b) => a.name.localeCompare(b.name));
}

export default function EventServicePage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [runtime, setRuntime] = useState<Runtime | null>(null);
  const [displaySeconds, setDisplaySeconds] = useState(0);
  const [confirm, setConfirm] = useState<'start' | 'end' | 'reset' | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorText, setErrorText] = useState('');
  const [scoreBusy, setScoreBusy] = useState<'left-add' | 'left-sub' | 'right-add' | 'right-sub' | 'undo' | null>(null);
  const [activePanel, setActivePanel] = useState<ActivePanel>('default');
  const [statusPlayers, setStatusPlayers] = useState<StatusPlayer[]>([]);
  const [statusLoading, setStatusLoading] = useState(false);

  const [statusText, setStatusText] = useState('');
  const [statusBusy, setStatusBusy] = useState<string | null>(null);
  const [statusConfig, setStatusConfig] = useState<StatusConfig | null>(null);
  const [selectedContext, setSelectedContext] = useState<string | null>(null);
  const [selectedRefinement, setSelectedRefinement] = useState<string | null>(null);

  const [manualStatusText, setManualStatusText] = useState('');
  const [manualStatusBusy, setManualStatusBusy] = useState<string | null>(null);

  const logPrefix = useMemo(() => `[event-service id=${String(id || '')}]`, [id]);

  const loadRuntime = useCallback(async (silent = false) => {
    if (!id) {
      console.warn(`${logPrefix} loadRuntime skipped because route param id is missing`);
      setErrorText('Missing event id in route');
      setLoading(false);
      return;
    }

    try {
      if (!silent) {
        setLoading(true);
      }

      const data = await getEventRuntime(id);

      if (!isRuntimeShape(data)) {
        setErrorText('Runtime response shape invalid');
        return;
      }

      setRuntime(data);
      setErrorText('');
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Unable to load event runtime';

      setErrorText(String(message));
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [id, logPrefix]);

  const loadStatusPlayers = useCallback(async (eventId: string) => {
    try {
      setStatusLoading(true);
      const players = await fetchEventPlayers(eventId);
      setStatusPlayers(players);
    } catch (error: any) {
      console.warn(`${logPrefix} unable to load status players`, error);
    } finally {
      setStatusLoading(false);
    }
  }, [logPrefix]);

  const loadStatusConfig = useCallback(async (eventId: string) => {
    try {
      const response = await fetchEventStatusConfig(eventId);
      setStatusConfig((response?.data || null) as StatusConfig | null);
    } catch (error: any) {
      console.warn(`${logPrefix} unable to load status config`, error);
      setStatusConfig(null);
    }
  }, [logPrefix]);

  const handleContextPick = useCallback((context: string) => {
    setSelectedContext(context);
    setSelectedRefinement(null);
    setStatusText('');
    setErrorText('');
  }, []);

  const handleRefinementPick = useCallback(async (context: string, refinement: string) => {
    if (!id) return;

    try {
      const response = await fetchEventStatusPhrase(id, {
        context,
        refinement,
      });

      const payload = response?.data || {};

      setSelectedContext(context);
      setSelectedRefinement(refinement);
      setStatusText(String(payload?.text || '').trim());
      setErrorText('');
    } catch (error: any) {
      setErrorText(
        error?.response?.data?.message ||
        error?.message ||
        'Unable to generate phrase',
      );
    }
  }, [id]);

  const resetStatusFlow = useCallback(() => {
    setSelectedContext(null);
    setSelectedRefinement(null);
    setStatusText('');
  }, []);

  const applyGeneratedStatusToOwners = useCallback(async (ownerIds: string[]) => {
    if (!id) return;

    const text = statusText.trim();

    if (!text || !ownerIds.length) {
      return;
    }

    try {
      setStatusBusy(ownerIds.length > 1 ? 'all' : ownerIds[0]);

      await fetchEventStatusApply(id, {
        ownerIds,
        text,
      });

      await loadStatusPlayers(id);
      resetStatusFlow();
      setErrorText('');
    } catch (error: any) {
      setErrorText(
        error?.response?.data?.message ||
        error?.message ||
        'Unable to apply statement',
      );
    } finally {
      setStatusBusy(null);
    }
  }, [id, loadStatusPlayers, resetStatusFlow, statusText]);

  const applyManualStatusToOwners = useCallback(async (ownerIds: string[]) => {
    if (!id) return;

    const text = manualStatusText.trim();

    if (!text || !ownerIds.length) {
      return;
    }

    try {
      setManualStatusBusy(ownerIds.length > 1 ? 'all' : ownerIds[0]);

      await fetchEventStatusApply(id, {
        ownerIds,
        text,
      });

      await loadStatusPlayers(id);
      setManualStatusText('');
      setErrorText('');
    } catch (error: any) {
      setErrorText(
        error?.response?.data?.message ||
        error?.message ||
        'Unable to apply statement',
      );
    } finally {
      setManualStatusBusy(null);
    }
  }, [id, loadStatusPlayers, manualStatusText]);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      setErrorText('Missing event id in route');
      return;
    }

    loadRuntime();

    const interval = setInterval(() => {
      loadRuntime(true);
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [id, loadRuntime]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!runtime) return;

      const { startedAt, accumulatedMs, running } = runtime.timer;

      let total = accumulatedMs;

      if (running && startedAt) {
        total += Date.now() - startedAt;
      }

      setDisplaySeconds(Math.floor(total / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [runtime]);

  useEffect(() => {
    if (!runtime) {
      return;
    }

    const { startedAt, accumulatedMs, running } = runtime.timer;
    let total = accumulatedMs;

    if (running && startedAt) {
      total += Date.now() - startedAt;
    }

    setDisplaySeconds(Math.floor(total / 1000));
  }, [runtime]);

  useEffect(() => {
    if (!id || !runtime) {
      return;
    }

    loadStatusPlayers(id);
    loadStatusConfig(id);
  }, [id, runtime, loadStatusPlayers, loadStatusConfig]);

  if (loading && !runtime) {
    return (
      <Box p={2}>
        <Typography>Loading event runtime…</Typography>
      </Box>
    );
  }

  if (!runtime) {
    return (
      <Box p={2}>
        <Typography color="error">{errorText || 'Event runtime unavailable.'}</Typography>
        <Button sx={{ mt: 2 }} variant="contained" onClick={() => navigate(-1)}>
          Back
        </Button>
      </Box>
    );
  }

  const titleText =
    runtime.title ||
    runtime.baseName ||
    runtime.name ||
    'Event service';

  const teams = splitTeamsFromTitle(titleText);
  const leftTeamName = teams?.leftTeam || 'Left';
  const rightTeamName = teams?.rightTeam || 'Right';
  const scoreLeft = runtime.score?.left ?? 0;
  const scoreRight = runtime.score?.right ?? 0;

  const minutes = Math.floor(displaySeconds / 60);
  const seconds = displaySeconds % 60;
  const timerText = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  const isAdmin = runtime.permissions.canControlTimer;

  const phase =
    runtime.phase ||
    (runtime.inPlay
      ? 'during'
      : runtime.gamePeriod >= runtime.maxGamePeriods
        ? 'post'
        : 'pre');

  const phaseLabel =
    phase === 'pre'
      ? 'Pre-match'
      : phase === 'during'
        ? 'In play'
        : 'Post-match';

  const labels = runtime.labels || {
    currentPeriodName: 'Period',
    nextPeriodName: 'Next period',
    startActionLabel: 'Start',
    endActionLabel: 'End',
    gameOverLabel: 'Game over',
    continuous: false,
  };

  const showScorePanel = runtime.gameType === 'score';

  const periodSummary = labels.continuous
    ? 'Continuous clock'
    : `${labels.currentPeriodName} ${runtime.gamePeriod}/${runtime.maxGamePeriods}`;

  const primaryActionLabel = runtime.inPlay
    ? labels.endActionLabel || 'End period'
    : runtime.gamePeriod >= runtime.maxGamePeriods
      ? labels.gameOverLabel || 'Game over'
      : labels.startActionLabel || 'Start';

  const handleConfirmedAction = async () => {
    if (!id || !confirm) return;

    try {
      if (confirm === 'start') {
        await startEvent(id);
      } else if (confirm === 'end') {
        await endEvent(id);
      } else if (confirm === 'reset') {
        await resetGame(id);
      }

      setConfirm(null);
      await loadRuntime(true);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Unable to complete action';

      setErrorText(String(message));
      setConfirm(null);
    }
  };

  const handleScore = async (
    side: 'left' | 'right',
    delta: number,
    busyKey: 'left-add' | 'left-sub' | 'right-add' | 'right-sub',
  ) => {
    if (!id) return;

    try {
      setScoreBusy(busyKey);
      await adjustScore(id, side, delta);
      await loadRuntime(true);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Unable to adjust score';

      setErrorText(String(message));
    } finally {
      setScoreBusy(null);
    }
  };

  const handleUndoScore = async () => {
    if (!id) return;

    try {
      setScoreBusy('undo');
      await undoScore(id);
      await loadRuntime(true);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Unable to undo score';

      setErrorText(String(message));
    } finally {
      setScoreBusy(null);
    }
  };

  const handleAddGeneratedToPlayer = async (ownerId: string) => {
    await applyGeneratedStatusToOwners([ownerId]);
  };

  const handleAddGeneratedToAll = async () => {
    await applyGeneratedStatusToOwners(statusPlayers.map((player) => player.ownerId).filter(Boolean));
  };

  const handleAddManualToPlayer = async (ownerId: string) => {
    await applyManualStatusToOwners([ownerId]);
  };

  const handleAddManualToAll = async () => {
    await applyManualStatusToOwners(statusPlayers.map((player) => player.ownerId).filter(Boolean));
  };

  const confirmTitle =
    confirm === 'start'
      ? `${primaryActionLabel}?`
      : confirm === 'end'
        ? `${primaryActionLabel}?`
        : 'Reset score and timer?';

  const sideActions = [
    {
      key: 'default',
      label: 'Default',
      active: activePanel === 'default',
      variant: activePanel === 'default' ? 'contained' as const : 'outlined' as const,
      onClick: () => setActivePanel('default'),
    },
    {
      key: 'status',
      label: 'Status',
      active: activePanel === 'status',
      variant: activePanel === 'status' ? 'contained' as const : 'outlined' as const,
      onClick: () => setActivePanel('status'),
    },
    {
      key: 'score',
      label: showScorePanel ? 'Score live' : 'Score',
      active: activePanel === 'score',
      variant: activePanel === 'score' ? 'contained' as const : 'outlined' as const,
      onClick: () => setActivePanel('score'),
    },
    {
      key: 'posts',
      label: 'Posts',
      active: activePanel === 'posts',
      variant: activePanel === 'posts' ? 'contained' as const : 'outlined' as const,
      onClick: () => setActivePanel('posts'),
      disabled: true,
    },
    {
      key: 'undo',
      label: 'Undo score',
      variant: 'outlined' as const,
      onClick: handleUndoScore,
      disabled: !showScorePanel || scoreBusy !== null || !runtime.log.length,
    },
    {
      key: 'primary',
      label: primaryActionLabel,
      variant: runtime.inPlay ? 'contained' as const : 'outlined' as const,
      color: runtime.inPlay ? 'error' as const : 'success' as const,
      onClick: () => setConfirm(runtime.inPlay ? 'end' : 'start'),
      disabled: !isAdmin,
    },
    {
      key: 'reset',
      label: 'Reset',
      variant: 'outlined' as const,
      color: 'warning' as const,
      onClick: () => setConfirm('reset'),
      disabled: !isAdmin,
    },
  ];

  const firstOptions = Array.isArray(statusConfig?.firstOptions) ? statusConfig!.firstOptions : [];
  const refinementOptions = selectedContext
    ? Array.isArray(statusConfig?.refinements?.[selectedContext])
      ? statusConfig!.refinements[selectedContext]
      : []
    : [];
  const showContextButtons = !selectedContext;
  const showRefinementButtons = !!selectedContext && !selectedRefinement;
  const showAssignmentControls = !!statusText.trim();

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        bgcolor: '#f5f7fb',
        minHeight: '100vh',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '220px 1fr 260px' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: '1px solid #ddd',
            bgcolor: '#fff',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
            Event controls
          </Typography>

          <Box display="flex" flexDirection="column" gap={1}>
            {sideActions.map((action) => (
              <Button
                key={action.key}
                variant={action.variant}
                color={action.color}
                onClick={action.onClick}
                disabled={action.disabled}
                fullWidth
              >
                {action.label}
              </Button>
            ))}
          </Box>
        </Paper>

        <Box display="flex" flexDirection="column" gap={2}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: '1px solid #ddd',
              bgcolor: '#fff',
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {titleText}
            </Typography>

            <Typography variant="body1" color="text.secondary">
              {periodSummary} · {runtime.inPlay ? 'Live' : runtime.periodStatus || 'Ready'} · {phaseLabel}
            </Typography>
          </Paper>

          {activePanel === 'default' ? (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: '1px solid #ddd',
                bgcolor: '#fff',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 1.5,
                  mb: 2,
                }}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Default view
                  </Typography>
                  <Typography color="text.secondary">
                    RDOS-driven event buttons: choose first term, then refinement, then assign the generated statement.
                  </Typography>
                </Box>

                <Paper
                  elevation={0}
                  sx={{
                    px: 2,
                    py: 1,
                    border: '1px solid #ddd',
                    bgcolor: runtime.inPlay ? '#f2fff4' : '#fafafa',
                    minWidth: 120,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Timer
                  </Typography>
                  <Typography variant="h5" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                    {timerText}
                  </Typography>
                </Paper>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {showContextButtons && firstOptions.length ? (
                  <Box display="flex" flexDirection="column" gap={1}>
                    {firstOptions.map((item) => (
                      <Button
                        key={item.label}
                        variant="contained"
                        onClick={() => handleContextPick(item.label)}
                        sx={{
                          justifyContent: 'flex-start',
                          ...item.sx,
                        }}
                      >
                        {item.label}
                      </Button>
                    ))}
                  </Box>
                ) : null}

                {showRefinementButtons && refinementOptions.length ? (
                  <Box display="flex" flexDirection="column" gap={1}>
                    {refinementOptions.map((item) => (
                      <Button
                        key={`${selectedContext}-${item.label}`}
                        variant="contained"
                        onClick={() => handleRefinementPick(selectedContext!, item.label)}
                        sx={{
                          justifyContent: 'flex-start',
                          ...item.sx,
                        }}
                      >
                        {item.label}
                      </Button>
                    ))}
                  </Box>
                ) : null}

                {showRefinementButtons && selectedContext && !refinementOptions.length ? (
                  <Typography color="text.secondary">
                    No refinement options are available for {selectedContext}.
                  </Typography>
                ) : null}

                {showContextButtons && !firstOptions.length ? (
                  <Typography color="text.secondary">
                    No default event buttons are available for this event yet.
                  </Typography>
                ) : null}

                {showAssignmentControls ? (
                  <>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr auto auto' },
                        gap: 1,
                        mt: 1,
                      }}
                    >
                      <TextField
                        fullWidth
                        size="small"
                        label="Generated statement"
                        placeholder="Generated statement appears here"
                        value={statusText}
                        onChange={(event) => setStatusText(event.target.value)}
                      />
                      <Button
                        variant="outlined"
                        onClick={resetStatusFlow}
                        disabled={!statusText && !selectedContext && !selectedRefinement}
                      >
                        Reset
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleAddGeneratedToAll}
                        disabled={!statusText.trim() || !statusPlayers.length || statusBusy === 'all'}
                        sx={{ minWidth: 120 }}
                      >
                        All
                      </Button>
                    </Box>

                    {statusPlayers.length ? (
                      <Box display="flex" flexDirection="column" gap={1} sx={{ mt: 1 }}>
                        {statusPlayers.map((player) => (
                          <Button
                            key={player.ownerId}
                            variant="outlined"
                            onClick={() => handleAddGeneratedToPlayer(player.ownerId)}
                            disabled={!statusText.trim() || statusBusy === player.ownerId}
                            sx={{ justifyContent: 'space-between' }}
                          >
                            <span>{player.name}</span>
                            <span>{player.itemCount} item{player.itemCount === 1 ? '' : 's'}</span>
                          </Button>
                        ))}
                      </Box>
                    ) : (
                      <Typography color="text.secondary" sx={{ mt: 1 }}>
                        No player targets are available for this event yet.
                      </Typography>
                    )}
                  </>
                ) : null}
              </Box>
            </Paper>
          ) : activePanel === 'status' ? (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: '1px solid #ddd',
                bgcolor: '#fff',
              }}
            >
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Status
                </Typography>
                <Typography color="text.secondary">
                  Current player statements and free text entry.
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
                  gap: 1,
                  mb: 2,
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  label="Free text"
                  placeholder="Type a statement to add to one player or all players"
                  value={manualStatusText}
                  onChange={(event) => setManualStatusText(event.target.value)}
                />
                <Button
                  variant="contained"
                  onClick={handleAddManualToAll}
                  disabled={!manualStatusText.trim() || !statusPlayers.length || manualStatusBusy === 'all'}
                  sx={{ minWidth: 120 }}
                >
                  All
                </Button>
              </Box>

              {statusLoading ? (
                <Typography color="text.secondary">Loading players…</Typography>
              ) : !statusPlayers.length ? (
                <Typography color="text.secondary">
                  No teamKit owners were found for this event yet, so there are no player status rows to display.
                </Typography>
              ) : (
                <Box display="flex" flexDirection="column" gap={1.5}>
                  {statusPlayers.map((player) => (
                    <Paper
                      key={player.ownerId}
                      elevation={0}
                      sx={{
                        p: 1.5,
                        border: '1px solid #e1e4e8',
                        bgcolor: '#fafafa',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', md: '160px 1fr auto' },
                          gap: 1.5,
                          alignItems: 'start',
                        }}
                      >
                        <Box>
                          <Typography sx={{ fontWeight: 700 }}>{player.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {player.itemCount} teamKit item{player.itemCount === 1 ? '' : 's'}
                          </Typography>
                          {player.qdosItemIds.length > 0 && (
                            <Typography variant="body2" color="text.secondary">
                              QDOS ID{player.qdosItemIds.length === 1 ? '' : 's'}: {player.qdosItemIds.join(', ')}
                            </Typography>
                          )}
                        </Box>

                        <Box>
                          <Typography variant="body2">
                            {player.statement || 'No narrative statement yet.'}
                          </Typography>
                        </Box>

                        <Button
                          variant="outlined"
                          onClick={() => handleAddManualToPlayer(player.ownerId)}
                          disabled={!manualStatusText.trim() || manualStatusBusy === player.ownerId}
                        >
                          Add
                        </Button>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}
            </Paper>
          ) : activePanel === 'score' ? (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: '1px solid #ddd',
                bgcolor: '#fff',
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Score controls
              </Typography>

              <Typography color="text.secondary">
                Use the side controls to update the live score and undo the most recent scoring action.
              </Typography>
            </Paper>
          ) : (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: '1px solid #ddd',
                bgcolor: '#fff',
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Posts panel
              </Typography>

              <Typography color="text.secondary">
                This area is reserved for event posts.
              </Typography>
            </Paper>
          )}

          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: '1px solid #ddd',
              bgcolor: '#fff',
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Match clock
            </Typography>

            <Typography variant="h3" sx={{ fontVariantNumeric: 'tabular-nums', mb: 1 }}>
              {timerText}
            </Typography>

            <Typography color="text.secondary">
              {runtime.inPlay ? 'Clock running' : 'Clock stopped'}
            </Typography>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: '1px solid #ddd',
              bgcolor: '#fff',
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Scoreboard
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr auto 1fr',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="body1" color="text.secondary">
                  {leftTeamName}
                </Typography>
              </Box>

              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {scoreLeft} - {scoreRight}
              </Typography>

              <Box textAlign="right">
                <Typography variant="body1" color="text.secondary">
                  {rightTeamName}
                </Typography>
              </Box>
            </Box>

            {isAdmin && showScorePanel && (
              <>
                <Divider sx={{ my: 2 }} />

                <Box display="flex" gap={1} flexWrap="wrap">
                  <Button
                    variant="outlined"
                    onClick={() => handleScore('left', -1, 'left-sub')}
                    disabled={scoreBusy !== null || scoreLeft <= 0}
                  >
                    - {leftTeamName}
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={() => handleScore('right', -1, 'right-sub')}
                    disabled={scoreBusy !== null || scoreRight <= 0}
                  >
                    - {rightTeamName}
                  </Button>
                </Box>
              </>
            )}
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: '1px solid #ddd',
              bgcolor: '#fff',
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Recent actions
            </Typography>

            {runtime.log.length ? (
              <Box display="flex" flexDirection="column" gap={1}>
                {runtime.log
                  .slice()
                  .reverse()
                  .map((item, index) => (
                    <Typography key={`${item.ts}-${index}`} variant="body1">
                      {new Date(item.ts).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                      })}{' '}
                      — {item.action}
                      {item.note ? ` (${item.note})` : ''}
                    </Typography>
                  ))}
              </Box>
            ) : (
              <Typography color="text.secondary">No service actions recorded yet.</Typography>
            )}
          </Paper>

          {errorText ? (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: '1px solid #f3b2b2',
                bgcolor: '#fff5f5',
              }}
            >
              <Typography color="error">{errorText}</Typography>
            </Paper>
          ) : null}
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: '1px solid #ddd',
            bgcolor: '#fff',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Score actions
          </Typography>

          {showScorePanel ? (
            <Box display="flex" flexDirection="column" gap={1}>
              <Button
                variant="contained"
                onClick={() => handleScore('left', 1, 'left-add')}
                disabled={scoreBusy !== null}
              >
                + {leftTeamName}
              </Button>

              <Button
                variant="contained"
                onClick={() => handleScore('right', 1, 'right-add')}
                disabled={scoreBusy !== null}
              >
                + {rightTeamName}
              </Button>
            </Box>
          ) : (
            <Typography color="text.secondary">
              This event type does not use score controls.
            </Typography>
          )}
        </Paper>
      </Box>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)}>
        <DialogTitle>{confirmTitle}</DialogTitle>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={confirm === 'end' ? 'error' : confirm === 'reset' ? 'warning' : 'success'}
            onClick={handleConfirmedAction}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
